/**
 * Boot context — the signed-in user's identity, role, own location account,
 * restriction flag and must-set-start-date flag in ONE round trip (public.app_boot_context(),
 * supabase/migrations/20261020130000_app_boot_context.sql;
 * docs/plans/2026-10-15-lms-load-speed.md §4 Phase 2).
 *
 * It replaces the serial gate chain get_my_staff → staff_roles →
 * location_accounts → staff_roles. The mapping below is the one
 * useCurrentUserStaff / getViewerContext / useRestrictedStaff applied before,
 * unchanged:
 *   absent staff_roles row → 'staff'; super_admin → 'admin'; every other
 *   registry role (accountant, manager, coach, office_manager, …) as is;
 *   permission flags default false; an own active location account makes the
 *   viewer a location viewer whether or not a staff row exists; the
 *   restriction flag fails OPEN (null/error → not restricted).
 *
 * There is exactly one boot path: rpc app_boot_context. Any RPC error throws
 * (never a null default); the boot query treats it as unresolved and the
 * gates fail closed.
 */

import { supabase } from '@/integrations/supabase/client';

export interface CurrentUserStaff {
  id: string;
  email: string;
  full_name: string | null;
  role?: string;
  // staff_master.location is text[].
  location?: string[] | null;
  can_view_salary?: boolean;
  can_edit_reviews?: boolean;
  start_date?: string | null;
}

/** get_my_staff() row as returned inside the boot payload. */
export interface BootStaffRow {
  id: string;
  email: string;
  full_name: string | null;
  role: string | null;
  location: string[] | null;
  start_date: string | null;
}

export interface BootRoleRow {
  role: string;
  can_view_salary: boolean | null;
  can_edit_reviews: boolean | null;
}

/** Raw app_boot_context() payload. */
export interface BootContextPayload {
  staff: BootStaffRow | null;
  role_row: BootRoleRow | null;
  location_code: string | null;
  /** null = the restriction check itself failed (fail open). */
  is_restricted: boolean | null;
  /**
   * Server-decided: the caller must fill their real first working day
   * (public.my_must_set_start_date(),
   * supabase/migrations/20261026153100_app_boot_context_must_set_start_date.sql).
   * Absent on a database without that migration — read as false.
   */
  must_set_start_date?: boolean | null;
}

/** What the boot hooks select from. */
export interface BootContext {
  /** null = signed in but no linked staff record (e.g. a location iPad). */
  staff: CurrentUserStaff | null;
  /** The caller's OWN active location account code, else null. */
  locationCode: string | null;
  isRestricted: boolean;
  /** Opens the blocking StartDateRequiredGate. Only a confirmed `true` counts. */
  mustSetStartDate: boolean;
}

export function mapBootPayload(payload: BootContextPayload): BootContext {
  const roleRow = payload.role_row;
  let staff: CurrentUserStaff | null = null;
  if (payload.staff) {
    // staff_roles is the reliable source; it overrides staff_master.role,
    // which mis-flags coaches / leavers as managers. An ABSENT row is by
    // design (teachers have none) and resolves to 'staff', never an error.
    const registryRole = roleRow?.role;
    const role = registryRole === 'super_admin' ? 'admin' : (registryRole ?? 'staff');
    staff = {
      ...payload.staff,
      role,
      can_view_salary: roleRow?.can_view_salary ?? false,
      can_edit_reviews: roleRow?.can_edit_reviews ?? false,
    } as CurrentUserStaff;
  }
  return {
    staff,
    locationCode: payload.location_code ? payload.location_code : null,
    // Fail open — a restriction check is a narrowing control on top of auth,
    // never the primary gate; RLS stays the real boundary.
    isRestricted: payload.is_restricted === true,
    mustSetStartDate: payload.must_set_start_date === true,
  };
}

function assertPayload(data: unknown): BootContextPayload {
  if (!data || typeof data !== 'object' || !('staff' in data) || !('location_code' in data)) {
    throw new Error('app_boot_context returned an unexpected payload');
  }
  return data as BootContextPayload;
}

export async function fetchBootContext(): Promise<BootContext> {
  // app_boot_context() isn't in the generated Supabase types yet — single
  // cast point.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await supabase.rpc('app_boot_context' as any);
  if (error) throw error;
  return mapBootPayload(assertPayload(data));
}
